import React, { createContext, ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectors } from 'state';

type logsContextType = {
  selectedRows: React.Key[];
  setSelectedRows: (selected: React.Key[]) => void;
  showOnlyActivePlayers: boolean;
  setShowOnlyActivePlayers: (showOnlyActivePlayers: boolean) => void;
  showPersonNames: boolean;
  setShowPersonNames: (showPersonNames: boolean) => void;
  stopAutoSelectingTop3Players: boolean;
  setStopAutoSelectingTop3Players: (val: boolean) => void;
  activeKey: string;
  setActiveKey: (
    key: 'instrument_selection' | 'available_benchmark_options'
  ) => void;
};

export const TrainerSessionContext = createContext<logsContextType>({
  selectedRows: [],
  // Using an empty function here makes sense, since you provide an empty, default value.
  // Otherwise, you have to check if setSelected is undefined every time you'd want to use it, which is annoying.
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setSelectedRows: () => {},
  showOnlyActivePlayers: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setShowOnlyActivePlayers: () => {},
  showPersonNames: true,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setShowPersonNames: () => {},
  stopAutoSelectingTop3Players: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setStopAutoSelectingTop3Players: () => {},
  activeKey: 'instrument_selection',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setActiveKey: () => {},
});

export function TrainerSessionContextWrapper(props: {
  children: ReactElement;
}) {
  const players = useSelector(selectors.playersList);
  const activePlayers = players.filter(player => player.deals !== 0);
  let defaultShowState = false;
  let defaultSelectedState: React.Key[] = [];
  if (players !== undefined && players[0] !== undefined) {
    defaultSelectedState = [players[0].id];
  }
  if (activePlayers !== undefined && activePlayers.length > 0) {
    defaultShowState = true;
    // defaultSelectedState = [activePlayers[0].id];
    defaultSelectedState = activePlayers
      .slice() // create a copy so you don't mutate the original array
      .sort((a, b) => b.last_portfolio_return - a.last_portfolio_return)
      .slice(0, 3)
      .map(player => player.id);
  }

  const [selectedRows, setSelectedRows] =
    useState<React.Key[]>(defaultSelectedState);
  const [showOnlyActivePlayers, setShowOnlyActivePlayers] =
    useState<boolean>(defaultShowState);
  const [showPersonNames, setShowPersonNames] = useState(true);
  const [stopAutoSelectingTop3Players, setStopAutoSelectingTop3Players] =
    useState<boolean>(false);
  const [activeKey, setActiveKey] = React.useState<
    'instrument_selection' | 'available_benchmark_options'
  >('instrument_selection');

  useEffect(() => {
    if (
      activePlayers &&
      activePlayers.length > 0 &&
      !stopAutoSelectingTop3Players
    ) {
      const top3 = activePlayers
        .slice()
        .sort((a, b) => b.last_portfolio_return - a.last_portfolio_return)
        .slice(0, 3)
        .map(player => player.id);
      setSelectedRows(top3);
    }
  }, [players]);

  return (
    <TrainerSessionContext.Provider
      value={{
        selectedRows: selectedRows,
        setSelectedRows: setSelectedRows,
        showOnlyActivePlayers: showOnlyActivePlayers,
        setShowOnlyActivePlayers: setShowOnlyActivePlayers,
        showPersonNames,
        setShowPersonNames,
        stopAutoSelectingTop3Players: stopAutoSelectingTop3Players,
        setStopAutoSelectingTop3Players: setStopAutoSelectingTop3Players,
        activeKey: activeKey,
        setActiveKey: setActiveKey,
      }}
    >
      {props.children}
    </TrainerSessionContext.Provider>
  );
}
